import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, FileText, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  draft: "bg-zinc-200 text-zinc-800",
  review: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  published: "bg-primary text-primary-foreground",
};

export default function SOPLibrary() {
  const [sops, setSops] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const load = () => api.get("/sops").then((r) => setSops(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id, e) => {
    e?.stopPropagation();
    await api.delete(`/sops/${id}`);
    toast.success("Deleted");
    load();
  };

  const filtered = sops.filter((s) =>
    s.title.toLowerCase().includes(q.toLowerCase()) ||
    s.department.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="p-8 space-y-6" data-testid="sops-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-primary">/ STANDARD OPERATING PROCEDURES</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">SOP Library</h1>
          <p className="text-sm text-muted-foreground mt-1">Draft → Review → Approve → Publish.</p>
        </div>
        <Link to="/app/sops/builder">
          <Button data-testid="new-sop-btn" className="rounded-sm bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" /> New SOP with AI
          </Button>
        </Link>
      </div>

      <div className="flex items-center gap-2 border border-border rounded-sm px-3 py-2 max-w-md bg-card">
        <Search className="w-4 h-4 text-muted-foreground" />
        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search SOPs..."
          data-testid="sop-search"
          className="border-0 focus-visible:ring-0 h-8 p-0"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center" data-testid="sop-empty">
          <FileText className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="mt-4 font-display text-lg">No SOPs yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Create your first SOP with AI in seconds.
          </p>
          <Link to="/app/sops/builder">
            <Button className="mt-6 rounded-sm" data-testid="sop-empty-cta">
              <Plus className="w-4 h-4 mr-2" /> Generate SOP
            </Button>
          </Link>
        </div>
      ) : (
        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-3 overline text-muted-foreground">Title</th>
                <th className="px-4 py-3 overline text-muted-foreground">Department</th>
                <th className="px-4 py-3 overline text-muted-foreground">Status</th>
                <th className="px-4 py-3 overline text-muted-foreground">Version</th>
                <th className="px-4 py-3 overline text-muted-foreground">Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} onClick={() => nav(`/app/sops/${s.id}`)} className="border-b border-border hover:bg-secondary/50 cursor-pointer" data-testid={`sop-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium">{s.title}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.department}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${STATUS_COLOR[s.status]} rounded-sm font-mono-plex text-[10px] uppercase tracking-widest`}>
                      {s.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-mono-plex text-xs">v{s.version}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {new Date(s.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost" size="sm"
                      onClick={(e) => del(s.id, e)}
                      data-testid={`sop-delete-${s.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
